import inventoryDuck from '@/ducks/inventoryDuck.js';
import fraudReportDuck from '@/ducks/vdp/fraudReportDuck.js';
import vdpAvailableOffersDuck from '@/ducks/vdp/vdpAvailableOffersDuck.js';
import vdpExtendedHomeDeliveryDuck from '@/ducks/vdp/vdpExtendedHomeDeliveryDuck.js';
import vdpSimilarDealerListingsDuck from '@/ducks/vdp/vdpSimilarDealerListingsDuck.js';
import vdpSimilarListingsDuck from '@/ducks/vdp/vdpSimilarListingsDuck.js';
import vdpVideoDuck from '@/ducks/vdp/vdpVideoDuck.js';

import SimilarListingsFetcher from '@/fetchers/SimilarListingsFetcher';
import AvailableOffersFetcher from '@/fetchers/vdp/AvailableOffersFetcher';
import ExtendedHomeDeliveryFetcher from '@/fetchers/vdp/ExtendedHomeDeliveryFetcher';
import FraudReportFetcher from '@/fetchers/vdp/FraudReportFetcher';
import videoPlaylistFetcher from '@/fetchers/vdp/videoPlaylistFetcher';

/**
 * Sends a fraud report for a specific vehicle listing
 *
 * @param {Object} data - The fraud report data
 * @param {string|number} data.id - The listing ID
 * @param {string} data.vin - The Vehicle Identification Number
 * @param {Object} data.owner - The owner information
 * @param {Object} data.owner.location - The owner's location information
 * @param {string} data.owner.id - The owner's ID
 * @param {string} data.owner.name - The owner's name
 * @param {string} data.firstName - The reporter's first name
 * @param {string} data.lastName - The reporter's last name
 * @param {string} data.fraudType - The type of fraud being reported
 * @param {string} data.fraudSubType - The sub-type of fraud being reported
 * @param {string} data.explanation - The explanation of the fraud
 * @param {string} data.email - The reporter's email address
 * @param {string} data.phoneNumber - The reporter's phone number
 * @returns {Function} Redux thunk action
 */
export function sendFraud(data) {
    const requestData = {
        listingId: data.id.toString(),
        vin: data.vin,
        ownerState: data?.owner?.location?.address?.state ?? '',
        ownerCity: data?.owner?.location?.address?.city ?? '',
        ownerId: data.owner.id,
        ownerName: data.owner.name,
        firstName: data.firstName,
        lastName: data.lastName,
        fraudType: data.fraudType,
        fraudSubType: data.fraudSubType,
        explanation: data.explanation,
        email: data.email,
        phoneNumber: data.phoneNumber,
    };

    return (dispatch) => FraudReportFetcher(requestData)
        .then((response) => {
            if (response.errorMessage) {
                dispatch(fraudReportDuck.creators.showServerError(response.errorMessage));
            } else {
                dispatch(fraudReportDuck.creators.showSuccess(response));
            }
        }).catch((err) => {
            dispatch(fraudReportDuck.creators.showServerError(err.errorMessage));
        });
}

/**
 * Fetches available special offers for a vehicle listing
 *
 * @param {Object} query - The query parameters for fetching offers
 * @returns {Function} Redux thunk action that dispatches available offers to the store
 */
export function getAvailableOffers(query) {
    return (dispatch) => AvailableOffersFetcher(query)
        .then((data) => {
            dispatch(vdpAvailableOffersDuck.creators.setKey({
                key: 'availableOffers',
                value: data.specialOffers,
            }));
        });
}

/**
 * Internal helper function that fetches similar listings data and dispatches actions to store the results
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} search - Search criteria for similar listings
 * @param {Object} similarListingDuck - The Redux duck to use for storing similar listings (either dealer or general)
 * @returns {void}
 * @private
 */
function fetchSimilarListings(dispatch, search, similarListingDuck) {
    SimilarListingsFetcher(search)
        .then((data = {}) => {
            dispatch(inventoryDuck.creators.addInventory(data.listings ?? []));
            const similarListingIdsArray = (data.listings ?? []).map((listing) => listing.id);
            dispatch(similarListingDuck.creators.setActiveResults(similarListingIdsArray));
        });
}

/**
 * Builds search criteria object for similar listings based on a reference listing
 * Calculates price ranges and applies filters based on the listing characteristics
 *
 * @param {Object} listing - The reference vehicle listing
 * @param {string} channel - The brand channel being used
 * @param {boolean} isConquestable - Whether to include listings from other dealers
 * @param {boolean} hasPrivateSellerTypeParam - Whether the URL contains sellerType=p parameter
 * @returns {Object} The search criteria for similar listings
 * @private
 */
function getSimilarListingsCriteria(listing, channel, isConquestable, enableDefaultQueryParams, queryParams = {}, hasPrivateSellerTypeParam = false) {
    const { owner = {}, pricingDetail = {} } = listing;
    const zip = owner?.location?.address?.zip ?? '';
    const isPrivateSeller = owner?.privateSeller;

    let minPrice;
    let maxPrice;

    if (pricingDetail && (pricingDetail.salePrice || pricingDetail.incentive)) {
        const salePrice = pricingDetail.incentive || pricingDetail.salePrice;
        if (salePrice >= 30000) {
            minPrice = Math.round(salePrice * 0.95);
            maxPrice = Math.round(salePrice * 1.05);
        } else {
            minPrice = Math.round(salePrice * 0.90);
            maxPrice = Math.round(salePrice * 1.10);
        }
    }

    let criteria = {
        dealerId: ((isConquestable || isPrivateSeller) ? null : owner?.id),
        zip: (isConquestable || isPrivateSeller) ? zip : null,
        searchRadius: (isConquestable ? 50 : 100),
        excludedListingId: listing.id,
        makeCode: listing.makeCode,
        modelCode: listing.modelCode,
        classSeriesCode: listing.classSeriesCode,
        bodyStyleCode: listing.bodyStyleCodes,
        extColorSimple: listing.exteriorColorSimple,
        transmissionCode: listing.transmission?.name,
        driveCode: listing.driveGroup,
        listingType: listing?.type?.toUpperCase() ?? '',
        startYear: listing.year - 1,
        endYear: listing.year + 1,
        ...(hasPrivateSellerTypeParam && { sellerType: 'p' }),
        numRecords: 20,
        minPrice,
        maxPrice,
        minListingPriority: isConquestable ? 30 : null,
        // Override Preview channel to behave as ATC for testing L2 Retailing
        channel: channel === 'ATC,PREVIEW' ? 'ATC' : channel,
    };

    if (isConquestable) {
        // Do not send null, empty, or undefined value for ownerProduct query param
        // (can't use ternary for this param like we are for other params above)
        criteria.ownerProduct = 'INVENTORY_CONQUEST';
    }

    if (listing?.lifestyleCategories && listing.lifestyleCategories.includes('Commercial')) {
        // Add for commercial experience
        criteria.lifestyleCategory = 'Commercial';

    } else if (listing?.pricingDetail?.dealIndicator) {
        // Add for best-deals experience
        criteria.dealType = ['goodprice', 'greatprice'];
        criteria.excludedLifestyleCategory = 'Commercial';
    }

    if (enableDefaultQueryParams) {
        criteria = Object.assign(criteria, queryParams);
    }
    return criteria;
}

/**
 * Fetches similar vehicle listings based on the current listing
 * Selects the appropriate Redux duck based on whether we're showing dealer-specific inventory
 * Handles conditional logic for determining when to fetch and what criteria to use
 *
 * @param {Object} options - Options for fetching similar listings
 * @param {Object} options.listing - The current vehicle listing
 * @param {string} options.channel - The brand channel being used
 * @param {boolean} options.isSimilarListingsCarouselEnabled - Whether the second carousel is enabled by feature flag
 * @param {boolean} options.isSimilarDealerInventoryOnly - Whether we're showing only listings from the same dealer
 * @returns {Function} Redux thunk action that fetches and dispatches similar listings
 */
export function getSimilarListings({
    listing,
    channel,
    isSimilarListingsCarouselEnabled,
    isSimilarDealerInventoryOnly,
    enableDefaultQueryParams = false,
    queryParams = {},
    hasPrivateSellerTypeParam,
}) {
    return (dispatch, getState) => {
        const state = getState();
        const similarListingsDuck = isSimilarDealerInventoryOnly ? vdpSimilarListingsDuck : vdpSimilarDealerListingsDuck;
        const { initialLoad } = similarListingsDuck.selectors.getDuckState(state);
        const isFetchSimilarListings = isSimilarListingsCarouselEnabled && isSimilarDealerInventoryOnly;

        if (!initialLoad) return;

        if (isFetchSimilarListings) {
            fetchSimilarListings(dispatch, getSimilarListingsCriteria(listing, channel, true, enableDefaultQueryParams, queryParams), similarListingsDuck);
        } else {
            fetchSimilarListings(dispatch, getSimilarListingsCriteria(listing, channel, isSimilarListingsCarouselEnabled ? false : listing.isConquestable, enableDefaultQueryParams, queryParams, hasPrivateSellerTypeParam), similarListingsDuck);
        }
    };
}

/**
 * Fetches video playlist data for a vehicle listing
 *
 * @param {Object} query - The query parameters for fetching video playlist
 * @returns {Function} Redux thunk action that dispatches video playlist data to the store
 */
export function getVideoPlayList(query) {
    return (dispatch) => videoPlaylistFetcher(query)
        .then((data) => {
            dispatch(vdpVideoDuck.creators.setActiveResults(data));
        });
}

/**
 * Fetches extended home delivery information for a vehicle listing
 * Includes error handling for API failures
 *
 * @param {Object} query - The query parameters for fetching extended home delivery data
 * @returns {Function} Redux thunk action that dispatches home delivery data to the store
 */
export function getExtendedHomeDelivery(query) {
    return (dispatch) => {
        try {
            ExtendedHomeDeliveryFetcher(query)
                .then((data) => {
                    dispatch(vdpExtendedHomeDeliveryDuck.creators.setKey({
                        key: 'extendedHomeDelivery',
                        value: data,
                    }));
                });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error Fetching Extended Home Delivery API.', error);
        }
    };
}

/**
 * Default export containing all VDP remote actions
 */
export default {
    getVideoPlayList,
    sendFraud,
    getAvailableOffers,
    getSimilarListings,
    getExtendedHomeDelivery,
};
